import React, { useState, useEffect } from 'react';

import { CircularProgress } from '@material-ui/core';
import PropTypes from 'prop-types';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import { withStyles } from '@material-ui/core/styles';
import { Auth } from 'aws-amplify';
import ContentHeader from '../components/ContentHeader';
import globalStyles from '../styles';
import PhoneNumberInput from '../components/PhoneNumberInput';
import {
    getInvalidPhoneNumberMessage,
    isValidPhoneNumber
} from '../helpers/errors';

const styles = theme => ({
    root: {
        background: '#fff',
        boxShadow: '0px 2px 5px 0px rgba(0,0,0,0.21)',
        border: '0',
        maxWidth: '600px',
        margin: '40px auto',
        position: 'relative',
        textAlign: 'center',
        minHeight: '300px',
        [theme.breakpoints.down('sm')]: {
            maxWidth: '100%',
            margin: '20px 15px'
        }
    },
    intro: {
        marginTop: 0,
        marginBottom: 40
    },
    form: {
        display: 'flex',
        flexDirection: 'column',
        maxWidth: '360px',
        margin: '20px auto 40px',
        padding: '20px 10px 40px'
    },
    input: {
        margin: '0 20px 20px',
        '&:invalid': {
            border: 'red solid 2px'
        }
    },
    message: {
        display: 'inline-block',
        margin: '130px auto'
    },
    spinner: {
        margin: '130px auto'
    },
    'error > div': {
        border: '1px solid red'
    },
    link: globalStyles.link,
    submit: {
        width: '200px',
        height: '56px',
        margin: '20px auto'
    }
});

const Account = ({ classes }) => {
    const [isLoading, setIsLoading] = useState(true);

    const [email, setEmail] = useState('');
    const [mobile, setMobile] = useState('');
    const [countryCode, setCountryCode] = useState(
        process.env.REACT_APP_DEFAULT_PHONE_COUNTRY || 'GB'
    );
    const [mobileErrorMessage, setMobileErrorMessage] = useState(null);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isSubmitError, setIsSubmitError] = useState(false);

    useEffect(() => {
        async function fetchAccountDetails() {
            try {
                const { attributes } = await Auth.currentAuthenticatedUser({
                    bypassCache: true
                });
                setEmail(attributes.email);
                setMobile(attributes.phone_number);
                setIsLoading(false);
            } catch (error) {
                setIsLoading(false);
            }
        }

        fetchAccountDetails();
    }, []);

    const onChangeMobile = number => {
        setMobile(number);
        setMobileErrorMessage(null);
    };

    const onSubmit = async e => {
        e.preventDefault();

        setIsSubmitError(false);

        if (!isValidPhoneNumber(mobile, countryCode)) {
            setMobileErrorMessage(getInvalidPhoneNumberMessage(countryCode));
            return;
        }

        setIsLoading(true);

        try {
            const user = await Auth.currentAuthenticatedUser();
            await Auth.updateUserAttributes(user, {
                phone_number: mobile
            });
            setIsLoading(false);
            setIsSubmitted(true);
        } catch (error) {
            setIsLoading(false);
            setIsSubmitError(true);
        }
    };

    if (isLoading) {
        return (
            <div className={classes.root}>
                <CircularProgress
                    id="loading-spinner"
                    className={classes.spinner}
                />
            </div>
        );
    }

    if (!isLoading && !email) {
        return (
            <div className={classes.root}>
                <span className={classes.message}>
                    Account details could not be loaded. Please try again.
                </span>
            </div>
        );
    }

    if (isSubmitted) {
        return (
            <div className={classes.root}>
                <ContentHeader text="Account updated" id="account" />
                <form className={classes.form} onSubmit={onSubmit}>
                    <p className={classes.intro} style={{ marginBottom: 10 }}>
                        Your account details have successfully been updated.
                    </p>
                    <Button
                        variant="outlined"
                        color="primary"
                        className={classes.submit}
                        type="submit"
                        onClick={() => {
                            setIsSubmitted(false);
                        }}
                    >
                        Account details
                    </Button>
                </form>
            </div>
        );
    }

    return (
        <div className={classes.root}>
            <ContentHeader text="Account" id="account" />

            <form className={classes.form} onSubmit={onSubmit}>
                <p className={classes.intro} style={{ marginBottom: 10 }}>
                    Keep your{' '}
                    <a
                        href="https://help.shuttleid.uk/en/articles/7851787-update-your-email-address"
                        target="_blank"
                        rel="noopener noreferrer"
                        className={classes.link}
                    >
                        email
                    </a>{' '}
                    and phone details up-to-date to ensure your transport
                    provider can reach you.
                </p>

                <p className={classes.intro}>
                    Your operator will be able to use your mobile number to send
                    SMS service alerts.
                </p>

                <TextField
                    autoComplete="email"
                    className={classes.input}
                    id="email"
                    type="email"
                    label="Your email"
                    InputLabelProps={{
                        shrink: true
                    }}
                    value={email}
                    variant="outlined"
                    disabled
                />
                <PhoneNumberInput
                    number={mobile}
                    onChange={onChangeMobile}
                    onCountryChange={setCountryCode}
                    errorMessage={mobileErrorMessage}
                />
                {isSubmitError && (
                    <p style={{ color: 'red' }}>
                        There was a problem updating your details. Please ensure
                        valid values and try again.
                    </p>
                )}
                <Button
                    variant="outlined"
                    color="primary"
                    className={classes.submit}
                    type="submit"
                >
                    Update
                </Button>
            </form>
        </div>
    );
};

Account.propTypes = {
    classes: PropTypes.shape().isRequired
};

export default withStyles(styles)(Account);
